import {
  FluidAlignment,
  FluidButtonConfig,
  FluidButtonType,
  FluidFlexAlignment,
  FluidSearchOptions,
  FluidTableAction,
  FluidTableFormButtonConfig,
  FluidVerticalPosition,
} from '@lmig/fluid-core';
import { FluidTableTranslationOptions } from '@lmig/fluid-core/lib/i18n/types/fluid-table-translation-options.type';
import { EventEmitter, h } from '@stencil/core';

import { FluidDropdownButton } from '../../fluid-dropdown-button/fluid-dropdown-button';
import { FluidTable } from '../fluid-table';
import { flattenHeaders } from '../internal/fluid-table-header.utils';

// ================================================================ //
// -- Control Bar components
export type TableControlsPlacement =
  | Exclude<FluidVerticalPosition, FluidVerticalPosition.MIDDLE>
  | 'top-and-bottom';

export const TableControlBar = (props: {
  host: Partial<FluidTable>;
  placement: TableControlsPlacement;
  identifier: string;
}) => {
  const { host, placement, identifier } = props;
  return (
    <fluid-grid identifier={identifier}>
      <fluid-grid-item colSm={8} identifier={identifier}>
        <TableFormActions
          host={host}
          placement={placement}
          actionsDisabled={host.actionsDisabled}
          identifier={identifier}
        />
      </fluid-grid-item>
      <fluid-grid-item
        colSm={4}
        pullRight
        identifier={identifier}></fluid-grid-item>
    </fluid-grid>
  );
};

export type TableAction = FluidTableAction & { defaultHandlerKey?: string };
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export type TableActionHandlerMap = Record<string, (...args) => unknown>;

export interface TableSearchBarProps {
  enableSearch: boolean;
  searchTable: Function;
  handleSearchTermUpdate: Function;
  searchTerm: string;
  searchOptions: FluidSearchOptions;
  tableActions: TableAction[];
  actionClicked: EventEmitter;
  defaultHandlers?: TableActionHandlerMap;
  fluidTranslate?: FluidTableTranslationOptions;
  identifier?: string;
  searchApplied?: boolean;
}

export const TableSearchBar = (props: TableSearchBarProps) => {
  const {
    enableSearch,
    searchTable,
    handleSearchTermUpdate,
    searchTerm,
    searchOptions,
    tableActions,
    actionClicked,
    defaultHandlers,
    fluidTranslate,
    identifier,
    searchApplied,
  } = props;

  const gridProps = {
    colSm: 12,
    colMd: 12,
    colLg: enableSearch ? 6 : 12,
    alignSelf: FluidFlexAlignment.CENTER,
  };

  const shouldRender: boolean = enableSearch || tableActions?.length > 0;

  let searchBarRef: HTMLFluidSearchBoxElement;

  const manualSearch = () =>
    searchBarRef.getSearchTerm().then((searchTerm: string) => {
      handleSearchTermUpdate(searchTerm);
      searchTable();
    });

  return (
    shouldRender && (
      <fluid-container
        margin='0.5em 0 0.5em'
        data-test-id={`${identifier}-fluid-container`}>
        <fluid-grid removeSpacing={true} identifier={identifier}>
          {/* Table Search */}
          <fluid-grid-item {...gridProps} identifier={identifier}>
            {enableSearch && (
              <fluid-search-box
                ref={(el: HTMLFluidSearchBoxElement) => (searchBarRef = el)}
                fluidTranslate={fluidTranslate?.searchBox}
                onSearch={() => manualSearch()}
                onSearchCleared={() => manualSearch()}
                onSearchTermUpdated={(searchTermEvent: CustomEvent<string>) => {
                  searchOptions?.dynamicSearch &&
                    handleSearchTermUpdate(searchTermEvent.detail);
                }}
                searchTerm={searchTerm}
                searchOptions={searchOptions}
                identifier={identifier}
                data-test-id={`${identifier}-fluid-search-box`}
                _searchApplied={searchApplied}
              />
            )}
          </fluid-grid-item>

          {/* Table Actions */}
          <fluid-grid-item
            {...gridProps}
            alignItems={FluidAlignment.RIGHT}
            alignSelf={FluidFlexAlignment.CENTER}
            identifier={identifier}>
            <TableActions
              tableActions={tableActions}
              actionClicked={actionClicked}
              defaultHandlers={defaultHandlers}
              identifier={identifier}
            />
          </fluid-grid-item>
        </fluid-grid>
      </fluid-container>
    )
  );
};

// ================================================================ //
// -- TableActions

export const TableActions = (props: {
  tableActions: TableAction[];
  actionClicked: EventEmitter;
  defaultHandlers?: TableActionHandlerMap;
  identifier: string;
}) => {
  const { tableActions, actionClicked, defaultHandlers, identifier } = props;
  const margins = (idx: number) => ({
    removeVerticalMargin: true,
    removeHorizontalMargin: idx === tableActions.length - 1,
  });

  const mapButtonConfigToDropdownButton = (
    buttonConfig: FluidButtonConfig
  ): Partial<FluidDropdownButton> => {
    const { icon, ...rest } = buttonConfig;
    return {
      ...rest,
      icon: icon?.key,
    };
  };

  const handleButtonClick = (action: TableAction) => {
    if (defaultHandlers && defaultHandlers[action.defaultHandlerKey]) {
      defaultHandlers[action.defaultHandlerKey](action);
    }
    actionClicked.emit({
      actionKey: action.actionKey,
      rowData: null,
    });
  };

  return tableActions?.map((action, idx) => {
    if (action.buttonConfig && !action.menuItems) {
      return (
        <fluid-button
          {...margins(idx)}
          id={action.identifier}
          data-test-id={`${identifier}-action-${action.actionKey}`}
          identifier={identifier}
          config={action.buttonConfig}
          onClick={() => handleButtonClick(action)}
        />
      );
    }
    if (action.menuItems && action.menuItems.length > 0) {
      return (
        <fluid-dropdown-button
          {...margins(idx)}
          id={action.identifier}
          data-test-id={`${identifier}-menu-${action.actionKey}`}
          {...mapButtonConfigToDropdownButton(action.buttonConfig)}
          menuItems={action.menuItems}
          onLinkClicked={(ev) => {
            actionClicked.emit({ actionKey: ev.detail });
          }}
        />
      );
    }
  });
};

/**
 * Renders the table state actions (Save/Cancel) when there is an expandable form or
 * editable cells present in the table
 * @param props
 * @constructor
 */
export const TableFormActions = (props) => {
  const { placement, host, identifier } = props;
  const {
    formActionClicked,
    commitRowMutations,
    resetRowMutations,
    commitOnValidSave,
    _headers,
    actionsDisabled,
    formActionsConfig,
  } = host;

  const { submitButtonConfig, cancelButtonConfig, additionalActions } =
    formActionsConfig;

  const submit = (
    formActionClicked: EventEmitter,
    commitOnValidSave: boolean,
    commitRowMutations: Function,
    host: HTMLElement
  ) => {
    formActionClicked.emit('SUBMIT');
    if (commitOnValidSave) commitRowMutations(host);
  };

  const cancel = (
    formActionClicked: EventEmitter,
    resetRowMutations: Function,
    host: HTMLElement
  ) => {
    formActionClicked.emit('CANCEL');
    resetRowMutations(host);
  };

  const setDisabled = (
    el: HTMLFluidButtonElement,
    actionsDisabled: boolean,
    overrideActions?: boolean,
    preventDisabled?: boolean
  ) => {
    if (!preventDisabled && (actionsDisabled || overrideActions)) {
      el?.setAttribute('disabled', 'true');
    } else {
      el?.removeAttribute('disabled');
    }
  };

  if (
    flattenHeaders(_headers).some((h) => !!h.editableCellConfig) &&
    formActionsConfig.showEditActions &&
    (formActionsConfig.editActionsPlacement === placement ||
      formActionsConfig.editActionsPlacement === 'top-and-bottom')
  ) {
    return [
      <fluid-button
        type={FluidButtonType.PRIMARY}
        label={submitButtonConfig?.label || 'Save'}
        config={submitButtonConfig}
        theme={host.theme}
        overrideGlobalTheme={!!host.theme}
        onClick={() =>
          submit(formActionClicked, commitOnValidSave, commitRowMutations, host)
        }
        ref={(el) =>
          setDisabled(
            el,
            actionsDisabled,
            false,
            submitButtonConfig?.preventDisabled
          )
        }
        data-test-id={`${identifier}-submit-btn`}
        identifier={identifier}
      />,
      <fluid-button
        type={FluidButtonType.SECONDARY}
        label={cancelButtonConfig?.label || 'Cancel'}
        config={cancelButtonConfig}
        theme={host.theme}
        overrideGlobalTheme={!!host.theme}
        ref={(el) =>
          setDisabled(
            el,
            actionsDisabled,
            false,
            cancelButtonConfig?.preventDisabled
          )
        }
        onClick={() => cancel(formActionClicked, resetRowMutations, props.host)}
        data-test-id={`${identifier}-cancel-btn`}
        identifier={identifier}
      />,
      (additionalActions as FluidTableFormButtonConfig[])?.map(
        (actionConfig) => {
          return (
            <fluid-button
              config={actionConfig}
              theme={host.theme}
              overrideGlobalTheme={!!host.theme}
              ref={(el) =>
                setDisabled(
                  el,
                  actionsDisabled,
                  false,
                  actionConfig?.preventDisabled
                )
              }
              onClick={() =>
                props.host.formActionClicked.emit(actionConfig.actionKey)
              }
              data-test-id={`${identifier}-additional-actions-btn`}
              identifier={identifier}
            />
          );
        }
      ),
    ];
  }
};
